import React, { useState } from "react";
import Dictionary from "../../../utils/Dictionary";
import dictionaryData from "../../../../constants/Data";

const Article = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseOver = (e) => {
    const word = e.target.innerText;
    const definition = dictionaryData[word];

    if (definition) {
      setHoveredWord({ word, definition });
      setPosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredWord(null);
    }
  };

  const handleMouseOut = () => {
    setHoveredWord(null);
  };
  return (
    <div className="mb-20">
      {hoveredWord && (
        <Dictionary
          word={hoveredWord.word}
          definition={hoveredWord.definition}
          position={position}
        />
      )}
      <div>
        <div>
          <h2 id="spac_s">SpaC</h2>
          <hr className="dividergeneral mb-5"></hr>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            SpaC is a subunit of the <span>Lactobacillus rhamnosus GG</span>{" "}
            <span>sortase-dependent pili</span>, SpaCBA. Its natural function is
            to adhere human <span>mucus</span> and <span>collagen</span>
            <a href="#reference1SpaC">
              <sup>1</sup>
            </a>
            . We use its binding capacities to act as an anchor for our{" "}
            <span>live biological therapeutic</span>.
          </p>

          <figure className="flex flex-col justify-center items-center gap-4 mb-10">
            <img
              style={{ width: "60%", height: "60%" }}
              alt="spac"
              id="figure-1-spac"
              src="https://static.igem.wiki/teams/4888/wiki/engineering/spaccrystal-structure.jpeg"
            />
            <figcaption
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}
            >
              <b>
                <u>Figure 1:</u>{" "}
              </b>
              <span>Crystal structure</span> of pilus adhesin, SpaC from
              Lactobacillus rhamnosus GG, PDB : 6M48
              <a href="#reference2SpaC_s">
                <sup>2</sup>
              </a>
            </figcaption>
          </figure>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            SpaC is composed of five <span>domains</span>, organized as a{" "}
            <span>stalk region</span> and a <span>binding region</span>. The
            stalk region is composed of three domains: D3, D4 and D5. The
            binding region is composed of two domains: D1 and D2, with the more
            outer binding domain, D2, being the one interacting with mucus{" "}
            <a href="#reference3SpaC">
              <sup>3</sup>
            </a>
            . It is composed of 836 <span>amino acids</span> and weights 90.86{" "}
            <span>kDa</span>.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            The engineering cycle is an iterative process aimed at solving
            engineering problems despite gaps in knowledge. Through iteration of
            this cycle, one will achieve engineering success.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In this section we describe how we went through iterations of the
            engineering cycle to equip our live biological therapeutic of an
            anchor. To be an optimal temporary biological anchor, SpaC should
            moor our live biological therapeutic to the intestinal mucus
            temporarily and be present at its surface in a stable configuration.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In this section we describe how we went through iterations of the
            engineering cycle to equip our live biotherapeutic product (LBP)
            with an anchor that will bind to mucus in the intestine, so that our
            LBP has time to collect any cadmium present in the gut. To be an
            optimal temporary biological anchor, SpaC should moor our live
            biotherapeutic product to the intestinal mucus temporarily and be
            present at its surface in a stable <span>configuration</span>. Our
            aim was to engineer this biological anchor to have the following
            criteria:
          </p>
          <ul style={{ listStyle: "inside" }}>
            <li>Mucus mooring</li>

            <li>Stable</li>

            <li>Present at the surface</li>

            <li>Temporary anchoring</li>
          </ul>
          <br />
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In the following sections we describe two engineering cycles we went
            through to create our temporary biological anchor. Through our two
            iterations of the engineering cycle, we met all four of our aims and
            successfully equipped our live biotherapeutic product with an
            optimal temporary biological mucus anchor.
          </p>

          <h2 className=" mt-20" id="cycle-1_s">
            Cycle 1
          </h2>
          <hr className="dividergeneral mb-5"></hr>

          <h3 className="mb-5" id="cycle-1-design_s">
            Design
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            Mucus mooring and stability are <span>intrinsic parameters</span> of
            a successful mucus binding protein. To achieve our first and second
            requirements, to bind mucus and be stable, we did a literature
            review and chose the best suited mucus binding protein for our
            purposes: SpaC.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            We then needed to address how we could <span>express</span> SpaC on
            the bacterias surface to meet our third requirement. To mediate its
            role of anchor, SpaC needs to be on the surface of the bacteria. We
            met this requirement by fusing SpaC to the well-known surface
            display protein Lpp-OmpA.{" "}
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            {" "}
            Finally, we addressed our fourth requirement, to mediate temporary
            anchoring. The anchoring of SpaC in the intestine should be only
            temporary to let the live biotherapeutic products be evacuated and
            remove the bound cadmium. Thanks to an expression{" "}
            <span>control system</span> composed of an IPTG inducible Lac
            operator coupled to a theophylline riboswitch, SpaC is only produced
            when the live biotherapeutic products is grown in the lab. The{" "}
            <span>IPTG inducible Lac operator system</span> allows us to control
            the <span>transcription</span> and the <span>theophylline</span>{" "}
            <span>riboswitch</span> allows us to control the{" "}
            <span>translation</span>. A double system allows to counteract the{" "}
            <span>leakiness</span> of the individual systems.
          </p>

          <p
            className="mb-20"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            Additionally, we had to think about testing requirements. When
            designing our anchor, we designed ways to check that the system
            works as expected. To check that SpaC is indeed expressed, we added
            a <span>Flag tag</span> at its <span>N-terminus</span>. The Flag tag
            allows to specifically retrieve our <span>fusion protein</span> with
            the appropriate antibody. To check that it is on the surface we
            added a <span>GFP-binding nanobody</span>
            <a href="#reference4SpaC">
              <sup>4</sup>
            </a>{" "}
            at its <span>C-terminus</span>. Indeed, we wanted to have a visual
            confirmation of the surface expression. However, as <span>GFP</span>{" "}
            has a large <span>molecular weight</span> (27 kDa), we decided to
            use a GFP-binding nanobody with the small molecular weight of only
            12.5 kDa instead of expressing GFP fused to SpaC.
          </p>

          <figure className="flex flex-col justify-center items-center gap-4">
            <img
              style={{ width: "60%", height: "60%" }}
              alt="spac"
              id="figure-2-spac"
              src="https://static.igem.wiki/teams/4888/wiki/engineering/spac.png"
            />
            <figcaption>
              <b>
                <u>Figure 2:</u>{" "}
              </b>
              SpaC design
            </figcaption>
          </figure>

          <h3 className="mb-5 mt-10" id="cycle-1-build_s">
            Build
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            After designing our anchor, we then needed to build it. The
            engineered <span>construct</span> composed of the T7{" "}
            <span>promoter</span>, the IPTG inducible Lac operator, the
            theophylline riboswitch, the fusion protein: Lpp-OmpA+Flag
            tag+SpaC+GFP-binding nanobody, followed by the LacI promoter plus
            the lac <span>inhibitor</span> gene, was <span>cloned</span> in the
            pTwist Kan High copy <span>vector</span> and synthetized by the
            Twist Bioscience company
            <a href="#reference5SpaC">
              <sup>5</sup>
            </a>
            , a member of the International Gene Synthesis Consortium. This
            selected vector contains a <span>kanamycin resistance gene</span>{" "}
            for selection of carrier bacteria.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            The resulting plasmid was then <span>transformed</span> into the{" "}
            <span>competent lab strain</span> E. Coli BL21 DE3 by our means,
            resulting in a testable engineered <span>biological system</span>.
          </p>

          <h3 className="mb-5 mt-10" id="cycle-1-test_s">
            Test
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In this test step of our first engineering cycle, we deciphered if
            our actual anchor design met our aims.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            We first tested if our fusion protein Lpp-OmpA-SpaC was expressed.
            To do so, we monitored its presence via <span>western blot</span>{" "}
            using an anti-Flag <span>antibody</span>.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup:
            <a href="../user-friendly/experiments#Checking-that-SpaC-is-expressed_s">
              {" "}
              Checking that SpaC is expressed{" "}
            </a>
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            At the same time, we investigated the temporary anchoring criterium
            of our design. We tested if the expression control system was
            operable and not <span>leaky</span>. Again, we monitored this thanks
            to western blots. The anchor presence was monitored for four{" "}
            <span>expression</span> conditions: IPTG, Theophylline, IPTG +
            Theophylline, and none.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#theophylline_s">
              {" "}
              Checking that our theophylline-IPTG system works{" "}
            </a>
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            We then tested the stability of SpaC via western blots. We monitored
            the SpaC protein quantity over a few days after expression was
            stopped.{" "}
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#Half-life-of-SpaC_s">
              {" "}
              Half life of SpaC
            </a>
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            Next, we tested the surface expression requirement. We used multiple
            assays: a <span>GFP-binding assay</span>, a{" "}
            <span>proteinase K digestion assay</span>, and an{" "}
            <span>immunofluorescence assay</span>.{" "}
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#Surface_s">
              {" "}
              Checking that SpaC is on the surface{" "}
            </a>
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In the GFP-binding assay, we used GFP as a SpaC marker. If SpaC was
            expressed on the surface, we intended to observe, via{" "}
            <span>fluorescence microscopy</span>, a stronger GFP signal due to
            the GFP-binding nanobody compared to a <span>negative control</span>
            .{" "}
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In the proteinase K digestion assay, we used proteinase K digestion
            as the proxy for surface expression. Only proteins facing the
            external environment would be digested. Hence, we expected to see a
            decrease in SpaC quantity if expressed at the surface. The decrease
            was monitored via western blot.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In the immunofluorescence assay, we used fluorescent antibodies
            directed towards the Flag-tag as a marker. Under a fluorescence
            microscope, bacteria harboring SpaC at the surface would emit a
            stronger light signal.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            Finally, we tested the mucus binding criterium. We leveraged{" "}
            <span>mucus coated transwells</span> to assess the capacity of our
            anchor to moor our live biotherapeutic product to mucus. The number
            of bacteria attached was monitored via <span>CFU counting</span>.
            Our LBP was expected to form more <span>colonies</span>.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#mucus_s">
              {" "}
              Checking that our bacteria is able to bind to mucus{" "}
            </a>
          </p>

          <h3 className="mb-5 mt-10" id="cycle-1-learn_s">
            Learn
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            From the above-mentioned experiments, we observed the correct
            expression and <span>induction</span> profile of our fusion protein,
            as well as anchor stability over multiple days. However, surface
            expression and mucus binding experiments were not conclusive. Taken
            together, those results indicated that our fusion protein is
            expressed under induction, but not transported to the surface where
            it should be.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            In our first engineering cycle we achieved stability and temporary
            anchoring, but not surface expression and mucus mooring. To meet the
            last two criteria, we conducted another engineering cycle, described
            in the next section.
          </p>

          <br />

          <h2 className="mt-20" id="cycle-2_s">
            Cycle 2
          </h2>
          <hr className="dividergeneral mb-5"></hr>

          <h3 className="mb-5" id="cycle-2-design_s">
            Design
          </h3>

          <p
            className="mb-20"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            After some discussion and literature review, we concluded that the
            large size of SpaC might be impairing surface export. The choice
            made in the first design to keep the whole SpaC{" "}
            <span>protein sequence</span> was motivated by the fact that its
            length helps to go deeper into mucus and enhance binding. We did not
            know the size limit for surface export, neither the modularity of
            the binding domains of SpaC. Because of this, we decided to design
            two shorter versions of our SpaC fusion protein: one with both the
            D1 and D2 domains and one with the D2 domain only. Those new designs
            resulted in a reduction of 41.03 <span>kDa</span> and 63.35 kDa,
            respectively. We expected those new designs to alleviate the size
            constraint of surface export.
          </p>

          <div className="flex justify-between">
            <figure className="flex flex-col gap-4 items-center justify-center">
              <img
                src="https://static.igem.wiki/teams/4888/wiki/engineering/d1d2.png"
                alt="d1d2 spac"
                id="figure-3-spac"
              />
              <figcaption>
                <b>
                  <u>Figure 3:</u>{" "}
                </b>
                Spac D1D2 design
              </figcaption>
            </figure>

            <figure className="flex flex-col gap-4 items-center justify-center">
              <img
                src="https://static.igem.wiki/teams/4888/wiki/engineering/d2.png"
                alt="d2 spac"
                id="figure-4-spac"
              />
              <figcaption>
                <b>
                  <u>Figure 4:</u>{" "}
                </b>
                Spac D2 design
              </figcaption>
            </figure>
          </div>
          <h3 className="mb-5 mt-10" id="cycle-2-build_s">
            Build
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            To shorten our original <span>plasmid</span>, we used two different{" "}
            <span>cloning</span> techniques. We used Hifi cloning for the D2
            shorter version and <span>KLD cloning</span> for the D1 plus D2
            shorter version. We <span>transformed</span> our shortened plasmids
            into the competent lab strain E. Coli BL21 DE3, resulting in new
            testable designs of our biological system.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#cloning_s">
              {" "}
              Cloning of SpaC construct{" "}
            </a>
          </p>

          <h3 className="mb-5 mt-10" id="cycle-2-test_s">
            Test
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            To investigate our two new designs, we first tested surface{" "}
            <span>expression</span> to assess if they overcame the surface
            export constraint we observed in our first engineering cycle. We
            re-performed the <span>immunofluorescence assay</span> described in
            engineering cycle 1 on both new designs.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#Immunofluorescence_s">
              {" "}
              Immunofluorescence assay{" "}
            </a>
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            At the same time, we tested the previously met criteria with the
            same experimental setups to ensure that our new designs did not
            alter the stability and temporality of SpaC. We verified that stable
            expression and temporary anchoring were still operational in both of
            our new designs.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#Half-life-of-SpaC_s">
              {" "}
              Half life of SpaC{" "}
            </a>{" "}
            and{" "}
            <a href="../user-friendly/experiments#theophylline_s">
              {" "}
              Checking that our theophylline-IPTG system works{" "}
            </a>
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            To test the mucus binding criterium, we again tested binding to
            mucus coated transwells with both shortened SpaC versions.
          </p>
          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            See corresponding experimental setup :
            <a href="../user-friendly/experiments#viability-assay_s">
              {" "}
              Evaluation of Plating Conditions for D2 and D1+D2 Domains in Mucus
              Binding Assays{" "}
            </a>
          </p>

          <h3 className="mb-5 mt-10" id="cycle-2-learn_s">
            Learn
          </h3>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            From the latter experiments, we confirmed once more that our
            construct allows the fusion protein expression when{" "}
            <span>induced</span>. Then we demonstrated that shortened versions
            of SpaC make it to the surface of the bacteria, hence resolving the
            size constraint imposed by the bacteria surface exporting system.
            Moreover, we demonstrated that surface expressed shortened versions
            of SpaC mediate bacteria mooring to the mucus.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            After this second engineering cycle, we achieved all four of our
            required criteria for having an optimal temporary biological anchor.
          </p>

          <p
            className="mb-5"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            Through two iterations of the engineering cycle, we equipped our
            live biotherapeutic product with an optimal temporary biological
            anchor, thereby achieving engineering success.
          </p>
        </div>
        <br />
        <h2 className="mt-20" id="References_s">
          <hr className="dividergeneral mb-5"></hr>
          References
        </h2>
        <ul>
          <ol type="1">
            <li id="reference1SpaC">
              Kankainen, Matti, et al. <br />
              <a href="https://pubmed.ncbi.nlm.nih.gov/19805152/">
                "Comparative genomic analysis of Lactobacillus rhamnosus GG
                reveals pili containing a human-mucus binding protein."
                Proceedings of the National Academy of Sciences 106.40 (2009):
                17193-17198.
              </a>
            </li>
            <li id="reference2SpaC">
              <a href="https://www.rcsb.org/structure/6M48">
                PDB page of the SpaC protein
              </a>
            </li>
            <li id="reference3SpaC">
              Kant, Abhiruchi, et al. <br />
              <a href="https://pubmed.ncbi.nlm.nih.gov/32653644/">
                "Crystal structure of lactobacillar SpaC reveals an atypical
                five-domain pilus tip adhesin: Exposing its substrate-binding
                and assembly in SpaCBA pili." Journal of Structural Biology
                211.3 (2020): 107571.
              </a>
            </li>
            <li id="reference4SpaC">
              Supplementary data of: Glass, David S., and Ingmar H.
              Riedel-Kruse. <br />
              <a href="https://pubmed.ncbi.nlm.nih.gov/30033369/">
                "A synthetic bacterial cell-cell adhesion toolbox for
                programming multicellular morphologies and patterns." Cell 174.3
                (2018): 649-658.
              </a>
            </li>
            <li id="reference5SpaC">
              <a href="https://www.twistbioscience.com/">
                Twist Bioscience web site
              </a>
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
